import produce from 'immer';
import React, {useEffect, useMemo} from 'react';

import {
  AnalysisTemplateDataFrameVariableGraphQL,
  AnalysisTemplateGraphQL,
} from 'analysis-tool/templates/AnalysisTemplateTypes';
import {assert} from 'Asserts';
import {SectionWrapper} from 'metadata/MetadataEditorInfoSection';
import AnalysisTemplateConfigContext, {
  AnalysisTemplateConfigContextValue,
} from 'procedures/design/analysis-template-config/AnalysisTemplateConfigContext';
import {
  AnalysisSourcesForConfigOrExecution,
  AnalysisSourcesOrigin,
  DatasetType,
} from 'procedures/design/ProcedureMethodAnalysisTypes';
import {ProcedureAnalysisTemplateConfiguration} from 'procedures/ProcedureDomainTypes';
import AnalysisTemplateConfigModalContext, {
  AnalysisTemplateConfigModalContextProperties,
  useAnalysisTemplateConfigModalContext,
} from 'procedures/shared-components/analysis/AnalysisTemplateConfigModalContext';
import {
  getColumnDefsFromDatasetId,
  isDataPresentForSourceDatasetId,
} from 'procedures/shared-components/analysis/preconfiguredAnalysisUtils';
import Modal from 'ui-library/modals/Modal';

import {isColumnMappingValid} from './AnalysisTemplateInputTableConfig';
import SingleMappingCard from './SingleMappingCard';

export interface BaseAnalysisTemplateConfigProps {
  analysisTemplate: AnalysisTemplateGraphQL;
  availableSources: AnalysisSourcesForConfigOrExecution;
  initialConfiguration: ProcedureAnalysisTemplateConfiguration;
  analysisTemplateConfigContext: AnalysisTemplateConfigContextValue;
}

export interface AnalysisTemplateConfigModalProps extends BaseAnalysisTemplateConfigProps {
  modalTitle: string;
  submitButtonText: string;
  onRequestSubmit: (newConfig: ProcedureAnalysisTemplateConfiguration) => void;
  onRequestClose: () => void;
}

export interface AnalysisTemplateConfigModalContentsProps extends BaseAnalysisTemplateConfigProps {
  analysisSourcesOrigin: AnalysisSourcesOrigin;
}

/**
 * @package Exported for testing
 */
export function areAllTablesProperlyMapped(
  config: ProcedureAnalysisTemplateConfiguration,
  availableSources: AnalysisSourcesForConfigOrExecution,
  variables: Array<AnalysisTemplateDataFrameVariableGraphQL>,
  analysisSourcesOrigin: AnalysisSourcesOrigin
): boolean {
  return (
    (analysisSourcesOrigin === AnalysisSourcesOrigin.CONFIGURATION ||
      // In execution, has every table required by the analysis template been configured?
      config.config.tableMappings.length === variables.length) &&
    // Is every configuration complete?
    config.config.tableMappings.every((oneTableMapping) => {
      const variableBeingConfigured = variables.find(
        (variable) => variable.id === oneTableMapping.analysisTemplateInputTableId
      );
      return (
        variableBeingConfigured != null &&
        isDataPresentForSourceDatasetId(oneTableMapping.datasetId, availableSources, analysisSourcesOrigin) &&
        isColumnMappingValid({
          variable: variableBeingConfigured,
          selectedInputTableInfo: {
            columns: getColumnDefsFromDatasetId(oneTableMapping.datasetId, availableSources) ?? [],
          },
          columnMapping: oneTableMapping.columnMappings,
        })
      );
    })
  );
}

export default function AnalysisTemplateConfigModal(props: AnalysisTemplateConfigModalProps): JSX.Element {
  const context = useAnalysisTemplateConfigModalContext(props.initialConfiguration);

  return (
    <Modal
      title={props.modalTitle}
      buttons={[
        {
          text: props.submitButtonText,
          submit: true,
          disabled: !context.canSubmit,
          tooltipTitle: null,
        },
      ]}
      onRequestSubmit={() => {
        assert(context.fullConfig != null, 'Expected a full config', {context});
        props.onRequestSubmit(context.fullConfig);
      }}
      onRequestClose={props.onRequestClose}
      dataTestAttrs={{component: 'AnalysisTemplateConfigModal', element: 'root'}}
      fullWidth={true}
    >
      <AnalysisTemplateConfigContext.Provider value={props.analysisTemplateConfigContext}>
        <AnalysisTemplateConfigModalContext.Provider value={context}>
          <SectionWrapper headerName="Automation template">
            <p>{props.analysisTemplate.name}</p>
          </SectionWrapper>

          <AnalysisTemplateConfigModalContents
            {...props}
            analysisSourcesOrigin={AnalysisSourcesOrigin.CONFIGURATION}
          />
        </AnalysisTemplateConfigModalContext.Provider>
      </AnalysisTemplateConfigContext.Provider>
    </Modal>
  );
}

export function AnalysisTemplateConfigModalContents(
  props: AnalysisTemplateConfigModalContentsProps
): JSX.Element {
  const configModalContext = AnalysisTemplateConfigModalContext.useContext();
  const analysisTemplateDataFrameVariables = useMemo(
    () => props.analysisTemplate.dataFrameVariables,
    [props.analysisTemplate]
  );
  const {fullConfig, setFullConfig, setCanSubmit} = configModalContext;

  useEffect(() => {
    setCanSubmit(
      fullConfig == null
        ? false
        : areAllTablesProperlyMapped(
            fullConfig,
            props.availableSources,
            analysisTemplateDataFrameVariables,
            props.analysisSourcesOrigin
          )
    );
  }, [
    analysisTemplateDataFrameVariables,
    setCanSubmit,
    fullConfig,
    props.analysisSourcesOrigin,
    props.availableSources,
  ]);

  return fullConfig == null ? (
    <></>
  ) : (
    <SectionWrapper headerName="Configure mapping">
      {analysisTemplateDataFrameVariables.map((variable) => {
        const inputTableId = variable.id;
        const tableMappingConfig = fullConfig.config.tableMappings.find(
          (tableMapping) => tableMapping.analysisTemplateInputTableId === inputTableId
        );
        return (
          <SingleMappingCard
            key={inputTableId}
            title={variable.name}
            variable={variable}
            availableSources={props.availableSources}
            analysisSourcesOrigin={props.analysisSourcesOrigin}
            tableMappingConfig={tableMappingConfig}
            onSelectDataset={(datasetId: string, datasetType: DatasetType) => {
              const newConfig = produce(fullConfig, (draftConfig) => {
                const tableMapping = draftConfig.config.tableMappings.find(
                  (tableMapping) => tableMapping.analysisTemplateInputTableId === inputTableId
                );
                if (tableMapping == null) {
                  draftConfig.config.tableMappings.push({
                    datasetId,
                    datasetType,
                    analysisTemplateInputTableId: inputTableId,
                    columnMappings: {},
                  });
                } else {
                  // Clear the column mappings when the dataset is changed
                  tableMapping.datasetId = datasetId;
                  tableMapping.datasetType = datasetType;
                  tableMapping.columnMappings = {};
                }
              });
              setFullConfig(newConfig);
            }}
            onChangeColumnMappings={(datasetId: string, newMappings: Record<string, string>) => {
              const newConfig = produce(fullConfig, (draftConfig) => {
                const tableMapping = draftConfig.config.tableMappings.find(
                  (tableMapping) => tableMapping.analysisTemplateInputTableId === inputTableId
                );
                assert(datasetId === tableMapping?.datasetId, 'Could not find table mapping', {
                  inputTableId,
                  datasetId,
                  tableMapping,
                });
                tableMapping.columnMappings = newMappings;
              });
              setFullConfig(newConfig);
            }}
          />
        );
      })}
      <AnalysisTemplateConfigModalContextRetriever contextValue={configModalContext} />
    </SectionWrapper>
  );
}

// Used for testing only
function AnalysisTemplateConfigModalContextRetriever(props: {
  contextValue: AnalysisTemplateConfigModalContextProperties;
}): null {
  return null;
}
